import prettyBytes from "pretty-bytes";
import type { ComposerFile } from "src/components/llm/utils/file-processing";
import { attachmentSize } from "src/components/llm/utils/attachment-validation";
import {
  Button,
  CloseIcon,
  Column,
  IconButton,
  ProgressBar,
  Row,
  Text,
  TextWithTooltip,
  Box,
} from "@hightouch/htui";
import type { AttachmentUpload } from "src/components/llm/context/file-context";

/** Upload details stay bounded even while a whole folder is queued. */
export const UploadProgressList = ({
  uploads,
  files,
  onRemove,
  onRetry,
}: {
  uploads: Map<string, AttachmentUpload>;
  files: ComposerFile[];
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
}) => {
  if (uploads.size === 0) return null;
  const entries = [...uploads.entries()];
  const failed = entries.filter(([, upload]) => upload.status === "failed");
  const completedCount = files.length;
  const totalCount = completedCount + uploads.size;
  const completedBytes = files.reduce(
    (bytes, file) => bytes + (file.file?.size ?? attachmentSize(file) ?? 0),
    0,
  );
  const loadedBytes =
    completedBytes +
    entries.reduce(
      (bytes, [, upload]) => bytes + Math.min(upload.loaded, upload.total),
      0,
    );
  const totalBytes =
    completedBytes +
    entries.reduce((bytes, [, upload]) => bytes + upload.total, 0);
  return (
    <Column width="100%" gap={2}>
      <Text size="sm">
        {completedCount} of {totalCount} files uploaded
        {totalBytes > 0 &&
          ` · ${prettyBytes(loadedBytes)} of ${prettyBytes(totalBytes)}`}
      </Text>
      <ProgressBar
        aria-label="Uploading attachments"
        value={
          totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0
        }
      />
      {failed.length > 0 && (
        <>
          <Text size="sm">Retry or remove failed uploads to send.</Text>
          <Column maxHeight="48" overflowY="auto" gap={2}>
            {failed.map(([id, upload]) => (
              <Row key={id} align="center" gap={2}>
                <Box flex={1} minWidth={0}>
                  <TextWithTooltip>{upload.fileName}</TextWithTooltip>
                  <Text size="sm" color="text.secondary">
                    {upload.error}
                  </Text>
                </Box>
                {upload.status === "failed" && onRetry && (
                  <Button
                    type="button"
                    size="sm"
                    variant="tertiary"
                    onClick={() => onRetry(id)}
                  >
                    Retry
                  </Button>
                )}
                {onRemove && (
                  <IconButton
                    type="button"
                    size="sm"
                    variant="tertiary"
                    icon={CloseIcon}
                    aria-label={`Remove ${upload.fileName}`}
                    onClick={() => onRemove(id)}
                  />
                )}
              </Row>
            ))}
          </Column>
        </>
      )}
    </Column>
  );
};
